import { useState } from 'react'
import { useLogout } from 'react-admin'
import { Navigate } from 'react-router-dom'
import {
  Button,
  Box,
  Typography,
  IconButton,
  InputAdornment
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import TextField from '@/components/TextField'
import useAlert from '@/components/Alert/hooks/useAlert'
import PasswordStrengthIndicator from '@/components/PasswordStrength'
import { getAccessTokenPayload } from '@/lib/jwt/decode'
import { isPasswordValid } from '@/lib/passwordValidation'
import {
  updateOrgUserPassword,
  UNKNOWN_PASSWORD_UPDATE_ERROR
} from '@/concepts/Account/repository/updateOrgUserPassword'

const UpdatePassword = () => {
  const { alertErrors } = useAlert()
  const logout = useLogout()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const jwtPayload = getAccessTokenPayload()
  if (!jwtPayload?.data?.attributes?.must_update_password) {
    return <Navigate to="/" replace />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (password !== confirmPassword) {
      alertErrors(['Passwords do not match.'])
      return
    }

    if (!isPasswordValid(password)) {
      alertErrors(['Password does not meet the requirements.'])
      return
    }

    setLoading(true)
    const result = await updateOrgUserPassword(
      password,
      jwtPayload.data.attributes.email
    )

    if (result.error) {
      alertErrors([
        result.error !== UNKNOWN_PASSWORD_UPDATE_ERROR
          ? result.error
          : 'Something went wrong. Please try again.'
      ])
      setLoading(false)
      return
    }

    sessionStorage.setItem(
      'flash_success',
      'Password updated successfully! Please sign in with your new password.'
    )
    await logout()
  }

  return (
    <Box
      sx={{
        maxWidth: 400,
        mx: 'auto',
        mt: 8,
        p: 4,
        boxShadow: 2,
        borderRadius: 2,
        backgroundColor: 'background.paper'
      }}
    >
      <Typography variant="h5" align="center" gutterBottom>
        Update Your Password
      </Typography>
      <Typography variant="body2" align="center" sx={{ mb: 2 }}>
        Your password needs to be updated to meet our new security requirements.
      </Typography>
      <form onSubmit={handleSubmit}>
        <TextField
          label="New Password"
          value={password}
          onChange={setPassword}
          type={showPassword ? 'text' : 'password'}
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        <PasswordStrengthIndicator password={password} />
        <TextField
          label="Confirm New Password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          type={showConfirmPassword ? 'text' : 'password'}
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle confirm password visibility"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        {confirmPassword.length > 0 && password !== confirmPassword && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            Passwords do not match.
          </Typography>
        )}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          disabled={
            loading ||
            !password ||
            !confirmPassword ||
            !isPasswordValid(password) ||
            password !== confirmPassword
          }
        >
          {loading ? 'Submitting...' : 'Update Password'}
        </Button>
      </form>
    </Box>
  )
}

export default UpdatePassword
